// DS6.20
import type { FC } from 'react';

import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { BiData, ManageInStudioClickBiEvent } from '@wix/da-bi/pkg/events';
import TextButtonWithContextMenu from '@wix/da-ds/pkg/Dropdown/Preset/TextButtonWithContextMenu';
import ArrowDown from '@wix/da-ds/pkg/Icons/24x24/ArrowDown';
import StudioProductsIcon from '@wix/da-ds/pkg/Icons/24x24/StudioProducts';
import StudioAnalyticsIcon from '@wix/da-ds/pkg/Icons/24x24/StudioAnalytics';
import StudioDiscountIcon from '@wix/da-ds/pkg/Icons/24x24/StudioDiscount';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { NullableDropdownItem } from '@wix/da-ds/pkg/Dropdown/types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';

export type ManagementDropdownType = 'shop' | 'subscriptions';

export interface Props {
  className?: string;
  type: ManagementDropdownType;
}

const ManagementDropdown: FC<Props> = ({ className, type }) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);

  const getDropdownItems = (): NullableDropdownItem[] => {
    const items: NullableDropdownItem[] = [
      {
        label: t('common.shopAnalytics'),
        icon: <StudioAnalyticsIcon size={IconSize.MEDIUM} />,
        onClick: () => {
          window.location.assign(Url.studio({ section: 'shop-reports' }));
        },
        biData: BiData<ManageInStudioClickBiEvent>({
          evid: 15,
          link_name: t('common.shopAnalytics'),
          link_url: Url.studio({ section: 'shop-reports' }),
          sectionname: type,
        }),
      },
      {
        label: t('common.discounts'),
        icon: <StudioDiscountIcon size={IconSize.MEDIUM} />,
        onClick: () => {
          const url =
            type === 'subscriptions' && isMobile
              ? Url.studio({
                  section: 'discounts',
                  discounts: 'active_and_scheduled',
                })
              : Url.studio({ section: 'discounts', discounts: 'active' });
          window.location.assign(url);
        },
        biData: BiData<ManageInStudioClickBiEvent>({
          evid: 15,
          link_name: t('common.discounts'),
          link_url:
            type === 'subscriptions' && isMobile
              ? Url.studio({
                  section: 'discounts',
                  discounts: 'active_and_scheduled',
                })
              : Url.studio({ section: 'discounts', discounts: 'active' }),
          sectionname: type,
        }),
      },
    ];

    if (type === 'shop') {
      items.unshift({
        label: t('common.products'),
        icon: <StudioProductsIcon size={IconSize.MEDIUM} />,
        onClick: () => {
          window.location.assign(Url.studio({ products: 'root' }));
        },
        biData: BiData<ManageInStudioClickBiEvent>({
          evid: 15,
          link_name: t('common.products'),
          link_url: Url.studio({ products: 'root' }),
          sectionname: type,
        }),
      });
    } else if (type === 'subscriptions') {
      items.unshift({
        label: t('common.subscriptionTiers'),
        icon: <StudioProductsIcon size={IconSize.MEDIUM} />,
        onClick: () => {
          window.location.assign(Url.studio({ products: 'tier' }));
        },
        biData: BiData<ManageInStudioClickBiEvent>({
          evid: 15,
          link_name: t('common.subscriptionTiers'),
          link_url: Url.studio({ products: 'tier' }),
          sectionname: type,
        }),
      });
    }
    return items;
  };

  return (
    <TextButtonWithContextMenu
      variant="edit-purple"
      size="medium"
      postfix={ArrowDown}
      items={getDropdownItems()}
      className={className}
    >
      {t('common.manageInStudio')}
    </TextButtonWithContextMenu>
  );
};

ManagementDropdown.displayName = 'ManagementDropdown';

export default ManagementDropdown;
